import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useExternalStoreRuntime, type AppendMessage, type ExternalStoreAdapter, type ThreadMessage, type ThreadSuggestion } from '@assistant-ui/react';
import { buildRequestTarget, getUserErrorMessage } from '@/shared/api';
import type { UploadLimits } from '@/app/providers/CapabilitiesProvider';
import type { ChatApi, PendingRunInteractionView, ThreadMessageRecordView } from './ChatRuntime';
import type { AgenthubInterrupt, AgenthubResumeEntry } from './agenthubInterrupts';
import type { AgenthubState } from './agenthubRuntimeStore';
import { createAgenthubAttachmentAdapter } from './attachmentAdapter';
import { appendMessageToThreadMessage, pendingInteractionToInterrupt, persistedRecordsToThreadMessages } from './nativeMessageAdapters';
import { agenthubRuntimeKey, useAgenthubRuntimeStore } from './agenthubRuntimeStore';
import { applyNativeEvent, markLastAssistantPaused } from './nativeThreadMessages';
import { NativeStreamClient, nativeMessages, type NativeEvent } from './nativeStream';

const EMPTY_MESSAGES: ThreadMessage[] = [];
const EMPTY_INTERRUPTS: AgenthubInterrupt[] = [];
const EMPTY_STATE: AgenthubState = {};

export type UseNativeAgenthubExternalRuntimeParams = {
  agentId: string;
  threadId: string | null;
  persistedMessages: ThreadMessageRecordView[];
  persistedPendingInteraction?: PendingRunInteractionView | null;
  api: ChatApi;
  baseUrl: string;
  streamTimeoutMs: number;
  uploadLimits?: UploadLimits;
  onThreadAccepted?: (threadId: string, runId?: string | null) => void;
  onRunFinished?: (runId?: string) => void;
  recoverActiveRunOnMount?: boolean;
  activeRunStartedAt?: number | null;
  activeRunId?: string | null;
  suggestions?: readonly ThreadSuggestion[];
};

export type NativeAgenthubRuntimeBinding = {
  assistantRuntime: ReturnType<typeof useExternalStoreRuntime>;
  interrupts: AgenthubInterrupt[];
  submitInterruptResponses: (responses: AgenthubResumeEntry[]) => Promise<void>;
  runExpired: boolean;
  restartConversation: () => Promise<void>;
};

function signature(records: readonly ThreadMessageRecordView[]): string {
  return records.map((record) => `${record.id}:${record.runId ?? ''}:${record.content.length}`).join('|');
}

function expired(error: unknown): boolean {
  return error instanceof Error && /run_expired|can no longer be resumed/i.test(error.message);
}

function reconnectable(error: unknown): boolean {
  return error instanceof Error && /stream ended|fetch|network|networkerror|503|502|504/i.test(error.message);
}

function lastAppliedSequence(events: readonly NativeEvent[], runId: string): number {
  return events
    .filter((event) => event.run_id === runId)
    .reduce((latest, event) => Math.max(latest, event.sequence), 0);
}

function runEventsUrl(baseUrl: string, runId: string): string {
  return buildRequestTarget(baseUrl, `/api/v1/runs/${encodeURIComponent(runId)}/events`);
}

// Events that end a pause: the answer was accepted (the run resumed) or the
// run reached a terminal state. Any of them in the replayed prefix means the
// persisted pending question is stale.
const PAUSE_RESOLVING_EVENTS = new Set(['resume_accepted', 'done', 'error']);

function pauseStillPending(events: readonly NativeEvent[], runId: string): boolean {
  return !events.some((event) => event.run_id === runId && PAUSE_RESOLVING_EVENTS.has(event.event_type));
}

// The user message whose run expired: the last user turn before the trailing
// assistant slot, or the last user turn when the transcript ends on the user.
function expiredRunPrompt(messages: readonly ThreadMessage[]): ThreadMessage | undefined {
  let lastUser: ThreadMessage | undefined;
  for (const message of messages) {
    if (message.role === 'user') lastUser = message;
  }
  const trailingAssistant = messages.at(-1)?.role === 'assistant' ? messages.length - 1 : -1;
  if (trailingAssistant < 0) return lastUser;
  for (let index = trailingAssistant - 1; index >= 0; index -= 1) {
    if (messages[index]?.role === 'user') return messages[index];
  }
  return lastUser;
}

export function useNativeAgenthubExternalRuntime(params: UseNativeAgenthubExternalRuntimeParams): NativeAgenthubRuntimeBinding {
  const { agentId, threadId, persistedMessages, persistedPendingInteraction = null, api, baseUrl, streamTimeoutMs,
    uploadLimits, onThreadAccepted, onRunFinished, recoverActiveRunOnMount = false, activeRunStartedAt = null, activeRunId = null,
    suggestions } = params;
  const key = agenthubRuntimeKey(agentId, threadId);
  const session = useAgenthubRuntimeStore((store) => store.sessions[key]);
  const clientRef = useRef<NativeStreamClient | null>(null);
  if (!clientRef.current) clientRef.current = new NativeStreamClient(
    buildRequestTarget(baseUrl, `/api/v1/agents/${encodeURIComponent(agentId)}/stream`),
    threadId,
    streamTimeoutMs,
  );
  const client = clientRef.current;
  const actions = useAgenthubRuntimeStore;
  const runtimeGeneration = useRef(actions.getState().runtimeGeneration).current;
  const runtimeIsCurrent = useCallback(
    () => actions.getState().runtimeGeneration === runtimeGeneration,
    [actions, runtimeGeneration],
  );
  const recoveryClientRef = useRef<NativeStreamClient | null>(null);
  const continuationClientRef = useRef<NativeStreamClient | null>(null);
  const isFirstTurnRef = useRef(persistedMessages.length === 0);
  const titleGeneratedRef = useRef(false);
  const acceptedThreadRef = useRef<string | null>(null);
  const recoveryPauseRef = useRef({ key, runId: activeRunId, pending: persistedPendingInteraction });
  if (recoveryPauseRef.current.key !== key || recoveryPauseRef.current.runId !== activeRunId) {
    recoveryPauseRef.current = { key, runId: activeRunId, pending: persistedPendingInteraction };
  }

  const generateTitleOnce = useCallback((finishedThreadId: string | null | undefined) => {
    if (!runtimeIsCurrent() || !finishedThreadId || !isFirstTurnRef.current || titleGeneratedRef.current) return;
    titleGeneratedRef.current = true;
    void api.generateTitle(finishedThreadId);
  }, [api, runtimeIsCurrent]);

  useEffect(() => {
    const abortClients = () => {
      client.abortRun();
      recoveryClientRef.current?.abortRun();
      continuationClientRef.current?.abortRun();
    };
    const unsubscribe = actions.subscribe((state) => {
      if (state.runtimeGeneration !== runtimeGeneration) abortClients();
    });
    if (!runtimeIsCurrent()) abortClients();
    return () => {
      unsubscribe();
      abortClients();
    };
  }, [actions, client, runtimeGeneration, runtimeIsCurrent]);

  useEffect(() => {
    if (!runtimeIsCurrent()) return;
    actions.getState().ensureSession(key, agentId, threadId);
    const hydrated = persistedRecordsToThreadMessages(persistedMessages);
    // A thread-level (orchestrator) question is pending: give the paused turn
    // the same settled assistant slot the live pause has, so ChatMessage can
    // render "Waiting for your answer" and withhold the ActionBar. A paused
    // run's assistant row is not persisted, so hydrated history alone ends on
    // the user's prompt. Specialist questions (toolCallId set) render on their
    // own card and need no thread-level slot.
    // Only that orchestrator pause goes in the thread-global interrupt slot
    // (mirroring the live path in `consume`): a specialist's question is
    // rebuilt on its own card from the replayed `user_question` event, and
    // seeding it globally too would render the same question twice.
    const orchestratorPause = persistedPendingInteraction !== null && !persistedPendingInteraction.toolCallId;
    actions.getState().hydrateSession(key, agentId, threadId, orchestratorPause ? markLastAssistantPaused(hydrated) : hydrated, signature(persistedMessages),
      orchestratorPause ? pendingInteractionToInterrupt(persistedPendingInteraction) : null);
  }, [actions, agentId, key, persistedMessages, persistedPendingInteraction, runtimeIsCurrent, threadId]);

  const acknowledgeThread = useCallback((acceptedThreadId: string, acceptedRunId: string | null = null) => {
    if (!runtimeIsCurrent() || acceptedThreadRef.current === acceptedThreadId) return;
    acceptedThreadRef.current = acceptedThreadId;
    onThreadAccepted?.(acceptedThreadId, acceptedRunId);
  }, [onThreadAccepted, runtimeIsCurrent]);

  const consume = useCallback((event: NativeEvent) => {
    if (!runtimeIsCurrent()) return;
    const state = actions.getState();
    const eventKey = key;
    if (!state.setNativeEvent(eventKey, event)) return;
    const current = state.sessions[eventKey]?.messages ?? [];
    if (event.thread_id) {
      // The first server event arrives only after the prompt transaction has
      // committed. Start the title request now, in parallel with refreshing
      // history, rather than waiting for the agent response to finish.
      if (current.some((message) => message.role === 'user')) generateTitleOnce(event.thread_id);
      acknowledgeThread(event.thread_id);
    }
    if (event.event_type === 'resume_accepted') {
      state.setInterrupts(eventKey, []);
      state.setRunning(eventKey, true);
      // Fall through: a specialist card renders its own pending question and
      // needs this event to clear it.
    }
    if (event.event_type === 'user_question' || event.event_type === 'approval_requested') {
      // A delegated specialist's question belongs to its card, not the
      // thread-global slot: the orchestrator and its other specialists are
      // still working, so the turn must keep running and a second specialist
      // asking must not overwrite the first one's question.
      const specialist = typeof event.payload.parent_tool_call_id === 'string' && event.payload.parent_tool_call_id;
      if (!specialist) {
        state.setInterrupts(eventKey, [{ id: event.event_id, eventId: event.event_id, reason: event.event_type, message: String(event.payload.question ?? event.payload.message ?? ''), metadata: { data: event.payload } }]);
        state.setRunning(eventKey, false);
        // Settle the per-message status too: `setRunning` only clears the
        // session-level flag, while the "Working…" timer/banner read the last
        // assistant message's own `status`. Leaving it `running` shows a
        // ticking clock next to a clarification card that is actually waiting
        // on the user (and on reload the replayed prefix ends marked running).
        state.setMessages(eventKey, markLastAssistantPaused(state.sessions[eventKey]?.messages ?? current));
        return;
      }
    }
    const terminal = event.event_type === 'done' || event.event_type === 'error';
    state.setMessages(eventKey, applyNativeEvent(current, event, !terminal, state.sessions[eventKey]?.runStartedAt ?? undefined));
    if (terminal) {
      state.setRunning(eventKey, false);
      state.setRunStarted(eventKey, null);
      state.setInterrupts(eventKey, []);
      onRunFinished?.(event.run_id);
    }
  }, [acknowledgeThread, actions, generateTitleOnce, key, onRunFinished, runtimeIsCurrent]);

  // Shared by run()'s mid-stream reconnect and recoverActiveRunOnMount's
  // resume-after-reload: up to 5 attempts, immediate first try then backoff,
  // classifying each attempt's error the same way. A single miss here (e.g.
  // the reattach lands before the worker has persisted its first event —
  // there is no local buffer to hit; the API releases it once a run is
  // handed to the worker) is a transient "not started yet", not a hard
  // failure, so it must retry rather than surface an error on attempt one.
  const recoverStream = useCallback(async (runId: string, fromNewPageLifetime = false): Promise<boolean> => {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      if (!runtimeIsCurrent()) return false;
      if (attempt > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, Math.min(1_000 * attempt, 5_000)));
        if (!runtimeIsCurrent()) return false;
      }
      const recovery = new NativeStreamClient(runEventsUrl(baseUrl, runId), client.threadId, streamTimeoutMs);
      recoveryClientRef.current = recovery;
      try {
        await recovery.run({
          messages: nativeMessages(actions.getState().sessions[key]?.messages ?? []),
          // A browser reload has no trustworthy in-memory projection. Replay
          // the full Durable Accepted Prefix instead of persisting a cursor
          // that could point beyond an event delivered before acceptance.
          afterSequence: fromNewPageLifetime
            ? 0
            : lastAppliedSequence(actions.getState().sessions[key]?.nativeEvents ?? [], runId),
        }, consume);
        return runtimeIsCurrent();
      } catch (recoveryError) {
        if (!runtimeIsCurrent()) return false;
        if (expired(recoveryError)) {
          actions.getState().setRunExpired(key, true);
          return false;
        }
        if (!reconnectable(recoveryError)) return false;
      }
    }
    return false;
  }, [actions, baseUrl, client, consume, key, runtimeIsCurrent, streamTimeoutMs]);

  const run = useCallback(async (resume?: AgenthubResumeEntry[], runConfig?: AppendMessage['runConfig']) => {
    if (!runtimeIsCurrent()) return;
    const connectionId = crypto.randomUUID();
    if (!actions.getState().acquireConnection(key, connectionId)) return;
    const current = actions.getState().sessions[key];
    actions.getState().setRunStarted(key, actions.getState().sessions[key]?.runStartedAt ?? Date.now());
    actions.getState().setRunning(key, true);
    try {
      await client.run(
        { messages: nativeMessages(current?.messages ?? []), resume, runConfig },
        consume,
        ({ threadId: acceptedThreadId, runId: acceptedRunId }) => acknowledgeThread(acceptedThreadId, acceptedRunId),
      );
    } catch (error) {
      if (!runtimeIsCurrent()) return;
      if (expired(error)) {
        actions.getState().setRunExpired(key, true);
      } else if (!(error instanceof DOMException && error.name === 'AbortError') && reconnectable(error) && client.threadId) {
        const runId = client.runId ?? actions.getState().sessions[key]?.nativeEvents.at(-1)?.run_id;
        const recovered = runId ? await recoverStream(runId) : false;
        if (!runtimeIsCurrent()) return;
        if (!recovered && !actions.getState().sessions[key]?.runExpired) {
          actions.getState().setMessageError(key, 'The live response was interrupted. Please retry.');
        }
      } else if (!(error instanceof DOMException && error.name === 'AbortError')) {
        actions.getState().setMessageError(key, getUserErrorMessage(error));
      }
    } finally {
      if (runtimeIsCurrent()) {
        actions.getState().setRunning(key, false);
        actions.getState().setRunStarted(key, null);
        actions.getState().releaseConnection(key, connectionId);
      }
    }
  }, [acknowledgeThread, actions, client, consume, key, recoverStream, runtimeIsCurrent]);

  const onNew = useCallback(async (message: AppendMessage) => {
    if (!runtimeIsCurrent()) return;
    actions.getState().appendMessage(key, appendMessageToThreadMessage(message));
    await run(undefined, message.runConfig);
  }, [actions, key, run, runtimeIsCurrent]);

  const submitInterruptResponses = useCallback(async (responses: AgenthubResumeEntry[]) => {
    if (!runtimeIsCurrent()) return;
    const connectionId = crypto.randomUUID();
    if (!actions.getState().acquireConnection(key, connectionId)) return;
    const activeThreadId = client.threadId;
    actions.getState().setRunning(key, true);
    let acceptedRunId: string | null = null;
    let continuation: NativeStreamClient | null = null;
    try {
      continuation = new NativeStreamClient(
        buildRequestTarget(baseUrl, `/api/v1/threads/${encodeURIComponent(activeThreadId)}/native-reattach`),
        activeThreadId,
        streamTimeoutMs,
      );
      continuationClientRef.current = continuation;
      await continuation.run(
        { messages: nativeMessages(actions.getState().sessions[key]?.messages ?? []), resume: responses },
        consume,
        ({ threadId: acceptedThreadId, runId }) => {
          if (!runtimeIsCurrent()) return;
          // The server has committed the answer once it accepts the resume
          // request. Clear the old pause here rather than waiting for the
          // streamed acknowledgement: an Argus continuation can briefly end
          // its SSE response between the resume acknowledgement and the next
          // supervisor event.
          acceptedRunId = runId;
          acknowledgeThread(acceptedThreadId, runId);
          actions.getState().setInterrupts(key, []);
        },
      );
    } catch (error) {
      if (!runtimeIsCurrent()) return;
      if (acceptedRunId) {
        // The answer was accepted, so a truncated continuation response is a
        // transport gap, not a failed user action. Tail the durable run stream
        // instead of leaving a resolved choice on screen for the user to
        // submit again.
        const recovered = await recoverStream(acceptedRunId);
        if (!runtimeIsCurrent()) return;
        if (!recovered && !actions.getState().sessions[key]?.runExpired) {
          actions.getState().setMessageError(key, 'The live response was interrupted. Please retry.');
        }
      } else if (expired(error)) {
        // The paused run's own wait-for-answer timeout fired before this
        // answer reached it (RunExpiredError from continuation.py). The choice
        // can never be submitted now, so surface the Timed-out/Restart state
        // instead of silently leaving the question card on screen (defect: a
        // ~40-minute-old clarification was answered and nothing happened).
        actions.getState().setRunExpired(key, true);
        actions.getState().setInterrupts(key, []);
      } else {
        actions.getState().setMessageError(key, getUserErrorMessage(error));
      }
    } finally {
      if (continuationClientRef.current === continuation) continuationClientRef.current = null;
      if (runtimeIsCurrent()) {
        actions.getState().setRunning(key, false);
        actions.getState().releaseConnection(key, connectionId);
      }
    }
  }, [acknowledgeThread, actions, baseUrl, client.threadId, consume, key, recoverStream, runtimeIsCurrent, streamTimeoutMs]);
  const restartConversation = useCallback(async () => {
    if (!runtimeIsCurrent()) return;
    actions.getState().setRunExpired(key, false);
    // The expired run belongs to the most recent user turn, not the thread's
    // first one: in a multi-turn thread the paused/settled assistant slot (if
    // any) trails that prompt, so take the last user message before it, or
    // simply the last user message when the transcript ends on the user.
    const message = expiredRunPrompt(actions.getState().sessions[key]?.messages ?? []);
    const text = message?.content.filter((part) => part.type === 'text').map((part) => part.text).join('');
    if (text) await onNew({ role: 'user', content: [{ type: 'text', text }], attachments: [], metadata: { custom: {} }, createdAt: new Date(), parentId: null, sourceId: null, runConfig: undefined });
  }, [actions, key, onNew, runtimeIsCurrent]);

  useEffect(() => {
    if (!runtimeIsCurrent() || !recoverActiveRunOnMount || !threadId || !activeRunId) return undefined;
    const persistedPauseAtRecoveryStart = recoveryPauseRef.current.pending;
    const connectionId = crypto.randomUUID();
    if (!actions.getState().acquireConnection(key, connectionId)) return undefined;
    const recoveredStart = activeRunStartedAt ?? Date.now();
    actions.getState().setRunStarted(key, actions.getState().sessions[key]?.runStartedAt ?? recoveredStart);
    actions.getState().setRunning(key, true);
    let aborted = false;
    void recoverStream(activeRunId, true)
      .then((recovered) => {
        if (aborted || !runtimeIsCurrent()) return;
        const recoveredSession = actions.getState().sessions[key];
        if (!recovered) {
          if (recoveredSession?.runExpired) {
            actions.getState().setInterrupts(key, []);
          } else {
            actions.getState().setMessageError(key, 'The live response was interrupted. Please retry.');
          }
          return;
        }
        // A paused run has a durable pending question but no live work to
        // stream: reattach replays its prefix and ends, which is a clean
        // "recovered" — not an interruption. Re-seed the question so the
        // clarification card comes back on reload, and settle the message so
        // it stops showing "Working…" next to a card that awaits the user.
        // `persistedPendingInteraction` is the mount-time snapshot, so guard
        // on what the replay just told us: if it carried a `resume_accepted`
        // or terminal event for this run, the question was answered (possibly
        // in another tab) and must not be resurrected over that state.
        if (persistedPauseAtRecoveryStart && !persistedPauseAtRecoveryStart.toolCallId
          && pauseStillPending(recoveredSession?.nativeEvents ?? [], activeRunId)) {
          actions.getState().setInterrupts(key, [pendingInteractionToInterrupt(persistedPauseAtRecoveryStart)]);
          actions.getState().setMessages(key, markLastAssistantPaused(actions.getState().sessions[key]?.messages ?? []));
        }
      })
      .finally(() => {
        if (runtimeIsCurrent()) {
          actions.getState().setRunning(key, false);
          actions.getState().setRunStarted(key, null);
          actions.getState().releaseConnection(key, connectionId);
        }
      });
    return () => {
      aborted = true;
      recoveryClientRef.current?.abortRun();
    };
  }, [actions, activeRunId, activeRunStartedAt, key, recoverActiveRunOnMount, recoverStream, runtimeIsCurrent, threadId]);

  const attachments = useMemo(() => createAgenthubAttachmentAdapter(() => uploadLimits, { baseUrl, agentId, getThreadId: () => client.threadId }), [agentId, baseUrl, client, uploadLimits]);
  const externalStore = useMemo<ExternalStoreAdapter<ThreadMessage>>(() => ({
    // Keep empty snapshots referentially stable. React's
    // useSyncExternalStore (used by assistant-ui) requires getSnapshot to
    // return the same value when the underlying runtime has not changed.
    messages: session?.messages ?? EMPTY_MESSAGES,
    isRunning: session?.isRunning ?? false,
    state: session?.state ?? EMPTY_STATE,
    adapters: { attachments }, onNew,
    onCancel: async () => {
      client.abortRun();
      if (!runtimeIsCurrent()) return;
      const id = threadId ?? client.threadId;
      if (id) await api.cancelThread(id).catch(() => undefined);
    },
    onImport: (messages) => {
      if (runtimeIsCurrent()) actions.getState().setMessages(key, [...messages]);
    },
    setMessages: (messages) => {
      if (runtimeIsCurrent()) actions.getState().setMessages(key, [...messages]);
    },
    suggestions,
  }), [actions, api, attachments, client, key, onNew, runtimeIsCurrent, session?.isRunning, session?.messages, session?.state, suggestions, threadId]);

  return { assistantRuntime: useExternalStoreRuntime(externalStore), interrupts: session?.interrupts ?? EMPTY_INTERRUPTS, submitInterruptResponses, runExpired: session?.runExpired ?? false, restartConversation };
}
